import React, { useState } from 'react'
import axios from 'axios'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'

import logo from '../../logo.png'

import {
  FiArrowLeft,
  FiCheckCircle,
  FiLock,
  FiMail,
  FiShield
} from 'react-icons/fi'


export default function ForgotPassword() {

  const { url } = useSelector(
    state => state
  )

  const [email, setEmail] =
    useState('')

  const [loading, setLoading] =
    useState(false)

  const [error, setError] =
    useState('')

  const [success, setSuccess] =
    useState(false)


  const submit = async (
    event
  ) => {

    event.preventDefault()

    setError('')


    const cleanEmail =
      email.trim().toLowerCase()


    if (!cleanEmail) {

      setError(
        'Please enter your email address.'
      )

      return
    }


    try {

      setLoading(true)


      const formData =
        new FormData()

      formData.append(
        'email',
        cleanEmail
      )


      const response =
        await axios.post(
          `${url}/auth/password-reset-link`,
          formData
        )


      if (
        response.data.status === 200
      ) {

        setSuccess(true)

        return
      }


      setError(
        response.data.error ||
        'Unable to process your request.'
      )


    } catch (err) {

      setError(
        err.response?.data?.detail ||
        err.response?.data?.error ||
        'Unable to process your request.'
      )

    } finally {

      setLoading(false)
    }
  }



  if (success) {

    return (

      <div className="
        min-h-screen
        bg-[#F7F9FC]
        font-[Satoshi]
        flex
        items-center
        justify-center
        px-5
      ">

        <div className="
          w-full
          max-w-md
          bg-white
          border
          border-gray-200
          rounded-2xl
          shadow-sm
          p-7
          sm:p-8
          text-center
        ">

          <div className="
            w-14
            h-14
            mx-auto
            rounded-full
            bg-green-50
            text-green-600
            flex
            items-center
            justify-center
            text-2xl
          ">
            <FiCheckCircle />
          </div>


          <h1 className="
            text-2xl
            font-bold
            text-gray-900
            mt-6
          ">
            Check your email
          </h1>


          <p className="
            text-sm
            text-gray-500
            leading-6
            mt-3
          ">
            If an account exists for{' '}
            <strong className="
              text-gray-700
            ">
              {email}
            </strong>,
            we've sent a password reset
            link to that address.
          </p>


          <div className="
            bg-gray-50
            rounded-xl
            p-4
            text-sm
            text-gray-500
            leading-6
            mt-6
          ">
            Didn't receive it? Check your
            spam folder or try again after
            a few minutes.
          </div>


          <Link
            to="/auth/signin/"
            className="
              h-12
              mt-6
              rounded-xl
              bg-[#101828]
              hover:bg-gray-800
              text-white
              font-bold
              flex
              items-center
              justify-center
              gap-2
              transition
            "
          >
            <FiArrowLeft />

            Back to sign in
          </Link>

        </div>

      </div>
    )
  }



  return (

    <div className="
      min-h-screen
      grid
      grid-cols-1
      lg:grid-cols-2
      font-[Satoshi]
    ">


      {/* LEFT SIDE */}

      <div className="
        hidden
        lg:flex
        bg-[#101828]
        text-white
        px-14
        xl:px-20
        py-14
        flex-col
        justify-between
      ">

        <div className="
          flex
          items-center
          gap-3
        ">

          <div className="
            w-11
            h-11
            rounded-xl
            overflow-hidden
            bg-white
          ">

            <img
              src={logo}
              alt="RentHub"
              className="
                w-full
                h-full
                object-cover
              "
            />

          </div>


          <span className="
            text-xl
            font-bold
          ">
            RentHub
          </span>

        </div>



        <div className="
          max-w-lg
        ">

          <div className="
            w-12
            h-12
            rounded-xl
            bg-green-500/10
            text-green-400
            flex
            items-center
            justify-center
            text-xl
            mb-7
          ">
            <FiLock />
          </div>


          <h1 className="
            text-4xl
            xl:text-5xl
            leading-tight
            font-bold
          ">
            Forgot your password?
          </h1>


          <p className="
            text-gray-400
            leading-7
            mt-5
          ">
            Enter your account email and
            we'll send you a secure link
            to create a new password.
          </p>

        </div>



        <div className="
          flex
          items-center
          gap-2
          text-sm
          text-gray-400
        ">

          <FiShield className="
            text-green-400
          " />

          Secure RentHub account recovery

        </div>

      </div>



      {/* FORM */}

      <div className="
        bg-white
        flex
        items-center
        justify-center
        px-5
        py-10
        sm:px-8
      ">

        <div className="
          w-full
          max-w-md
        ">


          {/* MOBILE LOGO */}

          <Link
            to="/"
            className="
              lg:hidden
              inline-flex
              items-center
              gap-3
              mb-10
            "
          >

            <div className="
              w-10
              h-10
              rounded-xl
              overflow-hidden
              border
              border-gray-100
            ">

              <img
                src={logo}
                alt="RentHub"
                className="
                  w-full
                  h-full
                  object-cover
                "
              />

            </div>


            <span className="
              text-xl
              font-bold
            ">
              RentHub
            </span>

          </Link>


          <Link
            to="/auth/signin/"
            className="
              inline-flex
              items-center
              gap-2
              text-sm
              text-gray-500
              hover:text-gray-900
              transition
              mb-7
            "
          >
            <FiArrowLeft />

            Back to sign in
          </Link>


          <h2 className="
            text-3xl
            font-bold
            tracking-tight
            text-gray-900
          ">
            Reset your password
          </h2>


          <p className="
            text-gray-500
            mt-2
            leading-6
          ">
            Enter the email address
            connected to your RentHub
            account.
          </p>


          <form
            onSubmit={submit}
            className="mt-8"
          >

            <label
              htmlFor="email"
              className="
                block
                text-sm
                font-semibold
                text-gray-700
                mb-2
              "
            >
              Email address
            </label>


            <div className="
              relative
            ">

              <FiMail className="
                absolute
                left-4
                top-1/2
                -translate-y-1/2
                text-gray-400
              " />


              <input
                id="email"
                type="email"
                value={email}
                onChange={
                  event =>
                    setEmail(
                      event.target.value
                    )
                }
                placeholder="you@example.com"
                autoComplete="email"
                className="
                  w-full
                  h-12
                  border
                  border-gray-300
                  rounded-xl
                  pl-11
                  pr-4
                  outline-none
                  focus:border-green-500
                  focus:ring-2
                  focus:ring-green-100
                  transition
                "
              />

            </div>


            {error && (

              <div className="
                bg-red-50
                border
                border-red-100
                text-red-600
                rounded-xl
                p-4
                text-sm
                mt-4
              ">
                {error}
              </div>

            )}


            <button
              type="submit"
              disabled={loading}
              className={`
                w-full
                h-12
                rounded-xl
                font-bold
                text-white
                mt-6
                transition

                ${
                  loading
                    ? `
                      bg-green-400
                      cursor-not-allowed
                    `
                    : `
                      bg-green-600
                      hover:bg-green-700
                    `
                }
              `}
            >

              {
                loading
                  ? 'Sending reset link...'
                  : 'Send reset link'
              }

            </button>

          </form>


          <p className="
            text-sm
            text-gray-500
            text-center
            mt-7
          ">

            Remember your password?{' '}

            <Link
              to="/auth/signin/"
              className="
                text-green-600
                font-semibold
                hover:text-green-700
              "
            >
              Sign in
            </Link>

          </p>

        </div>

      </div>

    </div>
  )
}